import { FloatingArrow } from '@floating-ui/react';
import styled, { css } from 'styled-components';

import Icon from '#@/components/Icon';
import { COLOR, mixins, textStyles } from '#@/shared/utils/styles';
import { zIndexValues } from '#@/shared/utils/styles/zIndexValues';

export const ARROW_WIDTH = 20;
export const ARROW_HEIGHT = 10;
export const ARROW_TIP_RADIUS = 4;
export const ARROW_PADDING = 15;
export const TOOLTIP_OFFSET = 5;
export const TOOLTIP_HORIZONTAL_PADDING = 20;
export const TOOLTIP_VERTICAL_PADDING = 20;
export const TOOLTIP_EDGE_OFFSET = 10;

export const MINI_TOOLTIP_OFFSET = 6;
export const MINI_TOOLTIP_EDGE_OFFSET = 10;

interface TooltipCardProps {
  $isOpen: boolean;
  $flexibleWidth?: boolean;
  $flexibleWidthConstrained?: boolean;
  // in milliseconds
  $transitionDelay: number;
  $addPadding: boolean;
}

const TOOLTIP_STANDARD_WIDTH = 300;

export const TooltipCard = styled.div<TooltipCardProps>`
  width: ${({ $flexibleWidth, $flexibleWidthConstrained }) =>
    $flexibleWidth || $flexibleWidthConstrained ? 'max-content' : `${TOOLTIP_STANDARD_WIDTH}px`};
  ${({ $flexibleWidthConstrained }) =>
    $flexibleWidthConstrained &&
    css`
      max-width: ${TOOLTIP_STANDARD_WIDTH}px;
    `}
  z-index: ${zIndexValues.overlayElement};

  border-radius: 8px;
  background-color: ${COLOR.white};
  ${(props) =>
    props.$addPadding &&
    css`
      padding: 0 ${TOOLTIP_HORIZONTAL_PADDING}px;
    `}
  filter: drop-shadow(0 1px 4px ${COLOR.boxShadow})
    drop-shadow(0 4px 20px ${COLOR.boxShadowLightAlpha});
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 0.2s ${(props) => props.$transitionDelay / 1000}s
    cubic-bezier(0.83, 0, 0.17, 1);
`;

interface MiniTooltipCardProps {
  $isOpen: boolean;
}

export const MiniTooltipCard = styled.div<MiniTooltipCardProps>`
  width: max-content;
  z-index: ${zIndexValues.overlayElement};

  padding: 4px 8px;
  border-radius: 5px;
  background-color: ${mixins.rgba(COLOR.textPrimary, 0.9)};
  color: ${COLOR.white};
  ${textStyles.bodySmall450};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 0.2s cubic-bezier(0.83, 0, 0.17, 1);
  pointer-events: none;
`;

export const CardArrow = styled(FloatingArrow)`
  fill: ${COLOR.white};
`;

interface TooltipTitleProps {
  $isWithIcon: boolean;
}

export const TooltipTitle = styled.div<TooltipTitleProps>`
  color: ${COLOR.textPrimary};
  ${textStyles.labelMedium500};

  &:only-child {
    ${({ $isWithIcon }) =>
      $isWithIcon &&
      css`
        ${textStyles.bodyMedium450};
      `};
  }
`;

export const TooltipBodyText = styled.div`
  color: ${COLOR.textSecondary};
  ${textStyles.bodyMedium450};
  user-select: none;
`;

export const TooltipAction = styled.div`
  ${textStyles.labelMedium500};
`;

export const TooltipSectionContainer = styled.div`
  align-items: center;
  display: flex;
  border-bottom: 1px solid ${COLOR.borderLight};
  padding: ${TOOLTIP_VERTICAL_PADDING}px 0;
  gap: 12px;

  &:last-child {
    border-bottom: 0;
  }
`;

export const TooltipSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const TooltipIcon = styled(Icon).attrs({ size: 16 })`
  flex-shrink: 0;
`;

export const Image = styled.img`
  max-width: 250px;
  max-height: 250px;
  margin: 12px;
`;
